import Vue from 'vue';
import VueI18n from 'vue-i18n';

import Api from '../services/api';
import fallbackTranslates from './translates/fallback.json';
import { useMemoize } from '@vueuse/core';

Vue.use(VueI18n);

const NO_LOCALE = 'none';
const FALLBACK_LOCALE = 'en';
export const NO_TRANSLATION_QUERY = 'translation-slugs'

let isInit = {};

const i18nInstance = new VueI18n({
  locale: NO_LOCALE,
});

const fetchTranslations = (locale: string, scope: string) => {
  return Api()
    .get('https://' + FRONT_CDN_HOST + `/translations/${scope}.${locale}.json`)
    .then(({ data }) => data);
};

export const i18n = i18nInstance;

export const hasNoTranslationParam = () => {
  if (process.env.DEPLOYMENT_ENV === 'prod') return false;
  return new URLSearchParams(window.location.search).has(NO_TRANSLATION_QUERY);
}

export const isI18nLoaded = () => i18nInstance.locale !== NO_LOCALE;

export const i18nLoadTranslations = (locale, translationMessages) => {
  i18nInstance.locale = locale;
  i18nInstance.mergeLocaleMessage(locale, translationMessages);
};

export const i18nSetPublicLocale = useMemoize((locale) => i18nSetLocale(locale, 'public'));
export const i18nSetMemberLocale = useMemoize((locale) => i18nSetLocale(locale, 'member'));

const mergeTranslations = async (locale: string, scope: string): Promise<void> => {
  const cacheKey = `${scope}-${locale}`;
  if (isInit[cacheKey]) {
    i18nInstance.mergeLocaleMessage(locale, isInit[cacheKey]);
    return;
  }
  try {
    const translations = await fetchTranslations(locale, scope);
    isInit[cacheKey] = translations;
    i18nInstance.mergeLocaleMessage(locale, translations);
  } catch {
    i18nInstance.mergeLocaleMessage(FALLBACK_LOCALE, fallbackTranslates);
  }
};

export async function i18nApplyLocale(locale: string): Promise<void> {
  document.querySelector('html')?.setAttribute('lang', locale);
  await Promise.all([mergeTranslations(locale, 'member'), mergeTranslations(locale, 'public')]);
  i18nInstance.locale = locale; // set only after BOTH scopes are fully merged
}

const i18nSetLocale = async (locale, scope) => {
  const cacheKey = `${scope}-${locale}`;
  if (isInit[cacheKey]) {
    i18nLoadTranslations(locale, isInit[cacheKey]);
    return isInit[cacheKey];
  }

  try {
    const translations = await fetchTranslations(locale, scope);
    isInit[cacheKey] = translations;
    i18nLoadTranslations(locale, translations);
  } catch (e) {
    i18nLoadTranslations(FALLBACK_LOCALE, fallbackTranslates);
  }
};
